import clsx from "clsx";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { BottomSheet } from "../../../BottomSheet";
import { ArrowDownIcon, BaseArrowDownIcon } from "../../../Icons";
import { Typo } from "../../../Typo";

export type SortOption = {
  id: string;
  label: string;
  /** Secondary line rendered beneath the label. Only meaningful for a grouped option — see `section`. */
  description?: string;
  /** Groups this option under a labelled section below the top-level (ungrouped) list, e.g. "Conversion rate".
   * Options without a `section` render ungrouped, in array order — existing callers are unaffected. */
  section?: string;
};

export type SortOrder = "asc" | "desc";

type MobileSortBottomSheetProps = {
  sortOptions: SortOption[];
  selectedSortBy: string;
  selectedSortOrder: SortOrder;
  onApply: (sortBy: string, sortOrder: SortOrder) => void;
  showSortOrder?: boolean;
  getOrderLabels?: (sortBy: string) => { descLabel: string; ascLabel: string };
  triggerLabel?: string;
  /** Overrides the default "Sort by" copy for the trigger's leading label. Also used as the sheet's header title, unless `sheetTitle` is set. */
  title?: string;
  /** Overrides the sheet's header title independently of `title` — for when the trigger copy and the sheet's title differ (e.g. trigger "Metrics" / sheet "View metrics"). */
  sheetTitle?: string;
  /** Renders the sheet with a drag handle instead of a close icon (matches newer bottom-sheet designs). */
  isDraggable?: boolean;
  /** Compact design scale: the trigger reads "Sort by <value> ⌄" at 12px without the colon, the sheet title is bold, and the sort-order control is an inset, borderless pill. */
  isCompact?: boolean;
};

export const MobileSortBottomSheet = ({
  sortOptions,
  selectedSortBy,
  selectedSortOrder,
  onApply,
  showSortOrder = true,
  getOrderLabels,
  triggerLabel,
  title,
  sheetTitle,
  isDraggable = false,
  isCompact = false,
}: MobileSortBottomSheetProps) => {
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [localSortBy, setLocalSortBy] = useState(selectedSortBy);
  const [localSortOrder, setLocalSortOrder] = useState(selectedSortOrder);

  const handleOpen = () => {
    setLocalSortBy(selectedSortBy);
    setLocalSortOrder(selectedSortOrder);
    setIsBottomSheetOpen(true);
  };

  const handleClose = () => {
    setIsBottomSheetOpen(false);
  };

  const { t } = useTranslation();
  const translatedContent = useMemo(() => {
    return {
      sortBy: t("clientsPage_sort_Bottomsheet_sortBy"),
      sortOrder: t("clientsPage_sort_Bottomsheet_sortOrder"),
      highToLow: t("clientsPage_sort_Bottomsheet_highToLow"),
      lowToHigh: t("clientsPage_sort_Bottomsheet_lowToHigh"),
      newestFirst: t("clientsPage_sort_Bottomsheet_newestFirst"),
      oldestFirst: t("clientsPage_sort_Bottomsheet_oldestFirst"),
      apply: t("clientsPage_sort_Bottomsheet_apply"),
    };
  }, [t]);

  const triggerTitle = title ?? translatedContent.sortBy;
  const triggerTypeStyle = isCompact ? "labelXS" : "labelSM";
  const segmentClassName = (isSelected: boolean) =>
    clsx(
      "flex-1 rounded-lg transition-colors",
      isCompact ? "h-7" : "h-8 border",
      isSelected ? "bg-surface-primary" : "bg-transparent",
    );
  const resolvedSheetTitle = sheetTitle ?? triggerTitle;

  const orderLabels = useMemo(() => {
    if (getOrderLabels) {
      return getOrderLabels(localSortBy);
    }
    return {
      descLabel: translatedContent.highToLow,
      ascLabel: translatedContent.lowToHigh,
    };
  }, [getOrderLabels, localSortBy, translatedContent]);

  const handleApply = () => {
    onApply(localSortBy, localSortOrder);
    setIsBottomSheetOpen(false);
  };

  const handleSortByChange = (sortId: string) => {
    setLocalSortBy(sortId);
  };

  const handleSortOrderChange = (order: SortOrder) => {
    setLocalSortOrder(order);
  };

  const currentSortLabel =
    triggerLabel ??
    sortOptions.find((opt) => opt.id === selectedSortBy)?.label ??
    "";

  const ungroupedOptions = sortOptions.filter((option) => !option.section);

  const sectionOrder: string[] = [];
  const optionsBySection = new Map<string, SortOption[]>();
  sortOptions.forEach((option) => {
    if (!option.section) return;
    if (!optionsBySection.has(option.section)) {
      sectionOrder.push(option.section);
      optionsBySection.set(option.section, []);
    }
    optionsBySection.get(option.section)!.push(option);
  });
  const sections = sectionOrder.map((label) => ({
    label,
    options: optionsBySection.get(label)!,
  }));

  const renderOption = (option: SortOption) => (
    <label key={option.id} className="flex cursor-pointer items-center gap-3">
      <div className="relative">
        <input
          type="radio"
          name="sortBy"
          value={option.id}
          checked={localSortBy === option.id}
          onChange={() => handleSortByChange(option.id)}
          className="peer sr-only"
        />
        <div className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-stroke-primary bg-surface-primary peer-checked:border-surface-inverse-primary peer-checked:bg-surface-inverse-primary">
          {localSortBy === option.id && (
            <div className="h-3 w-3 rounded-full bg-surface-primary" />
          )}
        </div>
      </div>
      {option.description ? (
        <div className="flex flex-col gap-0.5">
          <Typo type="bodySM" textColor="text-text-primary">
            {option.label}
          </Typo>
          <Typo type="captionXS" textColor="text-text-secondary">
            {option.description}
          </Typo>
        </div>
      ) : (
        <Typo type="bodySM" textColor="text-text-primary">
          {option.label}
        </Typo>
      )}
    </label>
  );

  return (
    <>
      <button
        onClick={handleOpen}
        data-testid="mobile_sort_bottom_sheet_trigger_button"
        className={clsx(
          "flex w-full flex-wrap items-center",
          isCompact ? "gap-x-1" : "gap-x-2",
        )}
      >
        <Typo
          type={triggerTypeStyle}
          textColor="text-text-primary"
          className="shrink-0"
        >
          {isCompact ? triggerTitle : `${triggerTitle}:`}
        </Typo>
        <span className="flex items-center gap-1">
          <Typo
            type={triggerTypeStyle}
            textColor="text-text-brand-default"
            className="line-clamp-1"
          >
            {currentSortLabel}
          </Typo>
          {isCompact ? (
            <BaseArrowDownIcon
              className="h-3 w-3 shrink-0 text-icon-tertiary"
              aria-hidden="true"
            />
          ) : (
            <ArrowDownIcon className="h-2 w-2 shrink-0" />
          )}
        </span>
      </button>

      <BottomSheet
        isOpen={isBottomSheetOpen}
        onClose={handleClose}
        title={resolvedSheetTitle}
        titleType={isCompact ? "headingMD" : undefined}
        isDraggable={isDraggable}
        footer={
          <button
            className="flex h-10 w-full cursor-pointer items-center justify-center rounded-lg bg-surface-brand-default lg:min-w-20 lg:px-3 lg:py-2"
            onClick={handleApply}
          >
            <Typo type="labelSM" textColor="text-text-inverse-primary">
              {translatedContent.apply}
            </Typo>
          </button>
        }
      >
        <div className={showSortOrder ? "mb-4" : ""}>
          <div className="space-y-3">{ungroupedOptions.map(renderOption)}</div>

          {sections.map((section) => (
            <div
              key={section.label}
              className="mt-3 border-t border-stroke-primary pt-3"
            >
              <Typo
                type="captionXS"
                textColor="text-text-tertiary"
                className="mb-3 block"
              >
                {section.label}
              </Typo>
              <div className="space-y-3">
                {section.options.map(renderOption)}
              </div>
            </div>
          ))}
        </div>

        {showSortOrder && (
          <div className="border-t border-stroke-primary pt-4">
            <div className="mb-3">
              <Typo type="labelSM" textColor="text-text-primary">
                {translatedContent.sortOrder}
              </Typo>
            </div>

            <div
              className={clsx(
                "flex rounded-[10px]",
                isCompact
                  ? "bg-surface-tonal-default p-0.5"
                  : "bg-stroke-primary",
              )}
            >
              <button
                onClick={() => handleSortOrderChange("desc")}
                data-testid="mobile_sort_bottom_sheet_order_desc_button"
                className={segmentClassName(localSortOrder === "desc")}
              >
                <Typo
                  type="labelXS"
                  textColor={
                    localSortOrder === "desc"
                      ? "text-text-primary"
                      : "text-text-tertiary"
                  }
                >
                  {orderLabels.descLabel}
                </Typo>
              </button>
              <button
                onClick={() => handleSortOrderChange("asc")}
                data-testid="mobile_sort_bottom_sheet_order_asc_button"
                className={segmentClassName(localSortOrder === "asc")}
              >
                <Typo
                  type="labelXS"
                  textColor={
                    localSortOrder === "asc"
                      ? "text-text-primary"
                      : "text-text-tertiary"
                  }
                >
                  {orderLabels.ascLabel}
                </Typo>
              </button>
            </div>
          </div>
        )}
      </BottomSheet>
    </>
  );
};
